import { ReactElement, ReactNode, useCallback } from 'react';

import {
   DurationInput,
   Timetable,
   TimetableDeviceType,
   TimetableEventRenderer,
   TimetableResourceTimeGridDayProps,
   TimetableTimeGridDayProps,
   TimetableTimeGridWeekProps,
} from '@dreryk/react-calendar';
import { getCurrentTimezone } from '@dreryk/react-common';
import { EmptyStatePlaceholder } from '@dreryk/react-components';

import { protectFeature } from '~libs.shared.system';

import {
   useBackgroundSlots,
   useEvents,
   useEventUpdater,
   useHoverConfiguration,
   useHoverSlots,
} from './AppointmentsTimetable.hooks';
import {
   AppointmentBackgroundSlotData,
   AppointmentEventSlotData,
   AppointmentHoverSlotData,
   AppointmentResource,
   AppointmentSchedule,
   AppointmentTimetableCurrentDate,
   AppointmentTimetableDateSetter,
} from './AppointmentsTimetable.types';
import {
   allowsSelectionOnlyWithinOneDate,
   disallowSelection,
   eventBackgroundConfiguration,
   filterCancelledAppointmentOrVisit,
   getEventMaxStack,
   VisitSettlementCalendarDataAccessor,
   withoutFilter,
} from './AppointmentsTimetable.utils';
import { AppointmentsTimetableEvent } from './timetable/event/AppointmentsTimetableEvent';
import { AppointmentsTimetableEventSelection } from './timetable/event/AppointmentsTimetableEventSelection';
import { AppointmentsTimetableSlotSelection } from './timetable/slot/AppointmentsTimetableSlotSelection';
import { dispatchAppointmentsTimetableSlotSelectionEventEvent } from './timetable/slot/AppointmentsTimetableSlotSelection.events';
import { translationKeys } from './translations';

type AppointmentsTimetableProps = {
   readonly selectedDate: AppointmentTimetableCurrentDate;
   readonly setSelectedDate: AppointmentTimetableDateSetter;
   readonly appointmentSchedules: AppointmentSchedule[] | null;
   readonly onEventChange: VoidFunction;
   readonly selectionOverlapping: boolean;
   readonly interactive: boolean;
   readonly nextAvailableTimeSlot?: AppointmentHoverSlotData;
   readonly preferCancelledVisitsVisible?: boolean;
   readonly onCancelledVisibilityChange?: (cancelledVisibility: boolean) => void;
   readonly noEventsPlaceholder?: ReactElement;
   readonly noResourcesPlaceholder?: ReactElement;
   readonly children?: ReactNode;
   readonly deviceType?: TimetableDeviceType;
   readonly actions?: ReactElement;
   readonly slotMinTime?: DurationInput;
   readonly slotMaxTime?: DurationInput;
};

export const AppointmentsTimetable = (
   props: AppointmentsTimetableProps &
      (
         | TimetableTimeGridWeekProps<AppointmentResource>
         | TimetableResourceTimeGridDayProps<AppointmentResource>
         | TimetableTimeGridDayProps<AppointmentResource>
      )
): JSX.Element => {
   const {
      children,
      onEventChange,
      appointmentSchedules,
      setSelectedDate,
      nextAvailableTimeSlot,
      preferCancelledVisitsVisible,
      deviceType,
      ...restProps
   } = props;
   const creatingAppointmentsAvailable = protectFeature('creatingAppointments');
   const resourceIdExtractor = useCallback(({ id }: AppointmentResource) => id.toString(), []);
   const eventContentRenderer = useCallback<TimetableEventRenderer<AppointmentEventSlotData>>(
      (event) => <AppointmentsTimetableEvent {...event} />,
      []
   );

   return (
      <>
         <Timetable<
            AppointmentResource,
            AppointmentEventSlotData,
            AppointmentBackgroundSlotData,
            AppointmentHoverSlotData
         >
            testId={'appointments'}
            {...restProps}
            resourceIdExtractor={resourceIdExtractor}
            onDateChange={setSelectedDate}
            backgroundSlots={useBackgroundSlots(appointmentSchedules)}
            hoverSlots={useHoverSlots(appointmentSchedules)}
            hoverConfiguration={useHoverConfiguration(nextAvailableTimeSlot)}
            events={useEvents(appointmentSchedules)}
            eventFilter={preferCancelledVisitsVisible ? withoutFilter : filterCancelledAppointmentOrVisit}
            onEventUpdate={useEventUpdater(onEventChange)}
            eventMaxStack={getEventMaxStack()}
            autoScrollToCurrentTime
            onSlotSelection={dispatchAppointmentsTimetableSlotSelectionEventEvent}
            allowsSelection={creatingAppointmentsAvailable ? allowsSelectionOnlyWithinOneDate : disallowSelection}
            dataAccessor={VisitSettlementCalendarDataAccessor}
            eventBackgroundConfiguration={eventBackgroundConfiguration}
            eventContentRenderer={eventContentRenderer}
            noResourcePlaceholder={
               props.noResourcesPlaceholder ?? (
                  <EmptyStatePlaceholder
                     testId={'noSchedule'}
                     heading={translationKeys.noWorkSchedulesAvailable}
                     subheading={translationKeys.noSpecialistHasAddedWorkingTimeOnThisDay}
                  />
               )
            }
            actions={props.actions}
            deviceType={deviceType}
            timeZone={getCurrentTimezone()}
            slotMinTime={props.slotMinTime}
            slotMaxTime={props.slotMaxTime}
            noEventsPlaceholder={props.noEventsPlaceholder}
         />
         {children}
      </>
   );
};

AppointmentsTimetable.SlotSelection = AppointmentsTimetableSlotSelection;
AppointmentsTimetable.EventSelection = AppointmentsTimetableEventSelection;
